import memoize from 'moize'

import arriereBackoffice, {
  CaseBasicDetailsUpdate,
  CasePriority,
  CaseRecordType,
  CaseSorting,
  CasesSearchPredefinedFilter,
  DateRangeInput,
  PaginationInput,
  SortOrder,
  UUID,
} from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { Alert } from '@avantstay/backoffice-core'

import { CaseColumnKeys } from '../utils/caseColumnKeys'
import { getEnrichmentColumnKeys, HOME_RATING_COLUMN_KEY } from './caseEnrichment'

const FieldMeta = {
  label: true,
  apiName: true,
}

const UserField = {
  id: true,
  name: true,
  email: true,
}

export interface SearchCasesProps {
  regionIds?: string[]
  statuses?: string[]
  taskTypes?: string[]
  priorities?: CasePriority[]
  searchTerm?: string
  dueDatePeriod?: DateRangeInput
  createdAtPeriod?: DateRangeInput
  homeIds?: string[]
  inStayGuestIssue?: boolean
  categories?: string[]
  subcategories?: string[]
  isRecurringCaseStatus?: boolean
  sortBy?: CaseSorting
  sortOrder?: SortOrder
  pagination?: PaginationInput
  showOffboarded?: boolean
  recordTypes?: CaseRecordType[]
  isAirbnbTripIssuesOnly?: boolean
  predefinedFilter?: CasesSearchPredefinedFilter
  caseOwnerIds?: string[]
  caseVendorIds?: string[]
  creatorIds?: string[]
  hasCaseOwner?: boolean
  includeEmptyCaseOwner?: boolean
  hasCaseVendor?: boolean
  includeEmptyCaseVendor?: boolean
  hasExpense?: boolean
  workOrderIds?: string[]
  skipTotal?: boolean
  taxonomyNodeIds?: string[]
}

const MAX_AGE_MEMOIZE = 60_000

interface SearchCasesParams {
  args: SearchCasesProps
  aliasPrefix?: string
  columnKeys?: string[]
}

export const searchCases = async ({ args, aliasPrefix = '', columnKeys }: SearchCasesParams) => {
  const enrichmentColumnKeys = columnKeys && getEnrichmentColumnKeys(columnKeys)
  const includeColumn = (columnKey: CaseColumnKeys) =>
    enrichmentColumnKeys == null || enrichmentColumnKeys.includes(columnKey)

  return arriereBackoffice.queries
    .cases({
      __alias: `${aliasPrefix ? `${aliasPrefix}_` : ''}operations_cases_searchCases`,
      searchCases: {
        __args: args,
        hasNextPage: true,
        values: {
          total: true,
          pages: true,
          results: {
            valueAddedServices: includeColumn(CaseColumnKeys.ValueAddedServices)
              ? {
                  serviceId: true,
                  serviceName: true,
                  earlyCheckInTime: true,
                  lateCheckOutTime: true,
                  bookingId: true,
                }
              : undefined,
            photoCompletion: true,
            aiGenerated: true,
            guestReadinessStatus: true,
            homeRating: includeColumn(HOME_RATING_COLUMN_KEY)
              ? {
                  ownerNps: true,
                  guestNps: true,
                  guestAirbnbRating: true,
                  guestVrboRating: true,
                }
              : undefined,
            revenueModel: includeColumn(CaseColumnKeys.RevenueModel),
            originalDueDate: true,
            isAirbnbTripIssue: true,
            workOrderId: true,
            status: true,
            taskType: true,
            category: true,
            subcategory: true,
            priority: true,
            subject: true,
            managedUnitId: true,
            dueDate: true,
            caseNumber: true,
            caseOwner: UserField,
            caseVendor: UserField,
            createdAt: true,
            createdByUserName: true,
            inStayGuestIssue: true,
            caseSfdcId: true,
            recordType: true,
            caseTaxonomy: {
              taxonomyId: true,
              task: true,
              serviceType: true,
              category: true,
              subcategory: true,
              status: { code: true },
            },
            recurringCaseSettingId: true,
            hasExpense: true,
            home: {
              assignedFieldOps: { name: true, vendorId: true },
              expenseAgreement: { mtMarkupPercent: true, spendApprovalThresholdInCents: true },
              id: true,
              name: true,
              region: { id: true, name: true },
            },
            stats: {
              dueDate: true,
              workMeanPercentage: true,
              photosMeanPercentage: true,
              initialWorkTime: true,
              lastUpdate: true,
              finalWorkTime: true,
            },
            lastReviewer: {
              name: true,
            },
            reviewStatus: true,
            openedFieldOpsCasesForProperty: includeColumn(CaseColumnKeys.OpenCases),
            managedUnitAvailabilityStats: includeColumn(CaseColumnKeys.PropertyStatus)
              ? {
                  status: true,
                  daysToNextCheckIn: true,
                }
              : undefined,
            caseBookingReviews: includeColumn(CaseColumnKeys.BookingRating)
              ? {
                  source: true,
                  reviewId: true,
                  categoryReviews: {
                    category: true,
                    rating: true,
                  },
                }
              : undefined,
            booking: {
              bookingId: true,
              hash: true,
              checkInDate: true,
              checkOutDate: true,
              guestName: true,
              guestsCount: true,
              nightsCount: true,
              source: true,
            },
          },
        },
      },
    })
    .then(response => {
      return response.searchCases
    })
    .catch(error => {
      if (args.skipTotal) throw error
      Alert.Error((error as Error).message)
      return {
        hasNextPage: undefined,
        count: {
          isgi: 0,
          notIsgi: 0,
        },
        countRecurringCases: {
          recurring: 0,
          notRecurring: 0,
        },
        values: {
          total: 0,
          pages: 0,
          results: [],
        },
      }
    })
}

export const findCaseBooking = async (workOrderId: UUID) => {
  return arriereBackoffice.queries
    .cases({
      __alias: 'operations_cases_findCaseBooking',
      find: {
        __args: { workOrderId },
        booking: {
          bookingId: true,
          checkInDate: true,
          checkOutDate: true,
          guestName: true,
          guestsCount: true,
          nightsCount: true,
        },
        workOrderId: true,
      },
    })
    .then(response => {
      return response.find
    })
    .catch(error => {
      Alert.Error((error as Error).message)
      return undefined
    })
}

export const updateCaseBasic = async (caseData: CaseBasicDetailsUpdate) => {
  return arriereBackoffice.mutations
    .cases({
      __alias: 'operations_cases_updateCaseBasic',
      updateCaseBasic: {
        __args: { caseUpdate: caseData },
      },
    })
    .then(results => {
      Alert.Success(`Case successfully updated.`)
      return results.updateCaseBasic
    })
    .catch(error => {
      Alert.Error((error as Error).message)
      return undefined
    })
}

export const getCaseInputs = memoize(
  async () => {
    return arriereBackoffice.queries
      .cases({
        __alias: 'operations_cases_getCaseInputsV2',
        getCaseInputsV2: {
          __args: {
            recordTypes: Object.values(CaseRecordType),
          },
          caseRecordType: true,
          inputs: {
            assignedFO: {
              id: true,
              name: true,
            },
            locationsInHome: FieldMeta,
            statuses: FieldMeta,
            caseTypes: {
              caseType: FieldMeta,
              categories: {
                category: FieldMeta,
                subcategories: FieldMeta,
              },
            },
          },
        },
      })
      .then(results => {
        return results.getCaseInputsV2
      })
      .catch(error => {
        Alert.Error((error as Error).message)
        return []
      })
  },
  { maxAge: MAX_AGE_MEMOIZE, isPromise: true, isDeepEqual: true },
)

export const searchCaseOwners = async (search: string) => {
  return arriereBackoffice.queries
    .vendors({
      __alias: 'operations_cases_searchCaseOwners',
      searchCaseOwners: {
        __args: {
          term: search,
        },
        id: true,
        name: true,
        email: true,
      },
    })
    .then(results => {
      return results.searchCaseOwners
    })
    .catch(error => {
      Alert.Error((error as Error).message)
      return undefined
    })
}

export const searchCaseVendors = async (search: string, conciergeOnly?: boolean) => {
  if (conciergeOnly) {
    return arriereBackoffice.queries
      .cases({
        __alias: 'operations_cases_searchCaseVendors',
        searchCaseVendors: {
          __args: {
            searchString: search,
            conciergeOnly: true,
          },
          id: true,
          name: true,
        },
      })
      .then(results => {
        return results.searchCaseVendors
      })
      .catch(error => {
        Alert.Error((error as Error).message)
        return undefined
      })
  }

  return arriereBackoffice.queries
    .vendors({
      __alias: 'operations_cases_searchVendorsByName',
      searchVendorsByName: {
        __args: {
          term: search,
        },
        id: true,
        name: true,
      },
    })
    .then(results => {
      return results.searchVendorsByName
    })
    .catch(error => {
      Alert.Error((error as Error).message)
      return undefined
    })
}

export const getOwners = memoize(
  async (recordTypes: CaseRecordType[]) => {
    return arriereBackoffice.queries
      .caseFilters({
        __alias: 'operations_cases_getOwnersFilter',
        caseOwners: {
          __args: {
            recordTypes,
          },
          id: true,
          name: true,
          // Roster matching for market scoping — see filterOwnerOptionsByRegions.
          email: true,
          // Market association, so the options can be narrowed to the selected
          // markets — see utils/filterOptionsByRegions, which keeps any option
          // this comes back empty for rather than hiding it.
          baseInfo: { regionIds: true },
        },
      })
      .then(results => {
        return results.caseOwners
      })
      .catch(error => {
        Alert.Error((error as Error).message)
        return Promise.resolve([])
      })
  },
  { maxAge: MAX_AGE_MEMOIZE, isPromise: true, isDeepEqual: true },
)

export const getVendors = memoize(
  async (recordTypes: CaseRecordType[]) => {
    return arriereBackoffice.queries
      .caseFilters({
        __alias: 'operations_cases_getVendorsFilter',
        vendors: {
          __args: {
            recordTypes,
          },
          id: true,
          name: true,
          // Market association, so the options can be narrowed to the selected
          // markets — see utils/filterOptionsByRegions, which keeps any option
          // this comes back empty for rather than hiding it.
          baseInfo: { regionIds: true },
        },
      })
      .then(results => {
        return results.vendors
      })
      .catch(error => {
        Alert.Error((error as Error).message)
        return Promise.resolve([])
      })
  },
  { maxAge: MAX_AGE_MEMOIZE, isPromise: true, isDeepEqual: true },
)

/**
 * Emails of everyone on the selected markets' rosters, lowercased — the
 * region-scoping signal for the Case Owner filter options, since owner vendor
 * records rarely carry regionIds (see filterOwnerOptionsByRegions). One
 * `staff.searchStaff` call per region (the endpoint is single-region; no
 * `period` — passing one 400s on some deployments). Returns null on any
 * failure so callers degrade to unscoped options instead of an empty filter.
 */
export const getStaffEmailsForRegions = memoize(
  async (regionIds: string[]): Promise<ReadonlySet<string> | null> => {
    try {
      const results = await Promise.all(
        regionIds.map(regionId =>
          arriereBackoffice.queries
            .staff({
              __alias: 'operations_cases_getRegionStaffEmails',
              searchStaff: {
                __args: { regionId },
                email: true,
              },
            })
            .then(response => response.searchStaff),
        ),
      )
      return new Set(
        results
          .flat()
          .map(member => member.email?.trim().toLowerCase())
          .filter((email): email is string => Boolean(email)),
      )
    } catch {
      return null
    }
  },
  { maxAge: MAX_AGE_MEMOIZE, isPromise: true, isDeepEqual: true },
)

export const getCreators = memoize(
  async (recordTypes: CaseRecordType[]) => {
    return arriereBackoffice.queries
      .caseFilters({
        __alias: 'operations_cases_getCreatorsFilter',
        creators: {
          __args: {
            recordTypes,
          },
          id: true,
          name: true,
        },
      })
      .then(results => {
        return results.creators
      })
      .catch(error => {
        Alert.Error((error as Error).message)
        return Promise.resolve([])
      })
  },
  { maxAge: MAX_AGE_MEMOIZE, isPromise: true, isDeepEqual: true },
)

export const getHomesSimpleList = memoize(
  async (name?: string, regionIds?: string[]) => {
    return arriereBackoffice.queries.homes
      .raw({
        __alias: 'getHomesSimpleList',
        __args: { name, regionIds, pagination: { page: 1, size: 5000 } },
        results: {
          id: true,
          name: true,
          title: true,
          isAncestor: true,
          region: {
            name: true,
          },
        },
      })
      .then(({ data }) => {
        return data.results
      })
      .catch(error => {
        Alert.Error((error as Error).message)
        return []
      })
  },
  { maxAge: MAX_AGE_MEMOIZE, isPromise: true, isDeepEqual: true },
)

export const getProperties = memoize(
  async (name?: string, regionIds?: string[]) => {
    return arriereBackoffice.queries.properties
      .raw({
        __alias: 'searchHomeToManagedUnits',
        searchHomeToManagedUnits: {
          __args: {
            searchString: name && name.length ? name : undefined,
            regionIds,
            excludeFinanciallyOffboarded: true,
          },
          results: {
            home: {
              id: true,
              name: true,
              title: true,
              isAncestor: true,
              region: {
                name: true,
              },
            },
          },
        },
      })
      .then(({ data }) => {
        return data.searchHomeToManagedUnits.results.map(property => property.home)
      })
      .catch(error => {
        Alert.Error((error as Error).message)
        return []
      })
  },
  { maxAge: MAX_AGE_MEMOIZE, isPromise: true, isDeepEqual: true },
)

export const getPropertyCases = memoize(
  async (homeIds?: string[]) => {
    return arriereBackoffice.queries.cases
      .raw({
        __alias: 'getPropertyCases',
        searchCases: {
          __args: {
            homeIds,
            isAirbnbTripIssuesOnly: true,
            sortBy: CaseSorting.createdAt,
            sortOrder: SortOrder.descending,
          },
          values: {
            results: {
              workOrderId: true,
              status: true,
              subject: true,
              dueDate: true,
              isAirbnbTripIssue: true,
            },
          },
        },
      })
      .then(({ data }) => {
        return data.searchCases.values.results
      })
      .catch(error => {
        Alert.Error((error as Error).message)
        return undefined
      })
  },
  { maxAge: MAX_AGE_MEMOIZE, isPromise: true, isDeepEqual: true },
)
